import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {COMMAND_PRIORITY_HIGH, PASTE_COMMAND, PasteCommandType} from 'lexical';
import {useEffect} from 'react';

/**
 * Plugin that strips Paper profile links (/ep/profile/:id) from pasted HTML.
 * These links are imported from Paper docs but aren't useful in Company OS.
 * The link text is preserved, only the link itself is removed.
 */
export const StripPaperProfileLinksPlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => {
        try {
          const clipboardData = (event as ClipboardEvent).clipboardData;
          if (!clipboardData) {
            return false;
          }

          const html = clipboardData.getData('text/html');
          if (!html) {
            return false;
          }

          // Check if the HTML contains Paper profile links
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          // Match links with /ep/profile/ in the href (Paper @mention links)
          const profileLinks = tempDiv.querySelectorAll('a[href*="/ep/profile/"]');

          if (profileLinks.length > 0) {
            // Replace each profile link with its text content
            profileLinks.forEach((link) => {
              const textContent = link.textContent || '';
              const textNode = document.createTextNode(textContent);
              link.parentNode?.replaceChild(textNode, link);
            });

            // Replace the clipboard data with processed HTML
            const processedHtml = tempDiv.innerHTML;
            const originalGetData = clipboardData.getData.bind(clipboardData);
            (clipboardData as DataTransfer).getData = (type: string) => {
              if (type === 'text/html') {
                return processedHtml;
              }
              return originalGetData(type);
            };
          }

          return false; // Let other handlers run
        } catch (error) {
          console.error('StripPaperProfileLinksPlugin error:', error);
          return false;
        }
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removePasteListener();
    };
  }, [editor]);

  return null;
};
